import {
  forwardRef,
  ButtonHTMLAttributes,
  ReactNode,
  isValidElement,
  cloneElement,
  Ref,
  ReactElement,
  HTMLAttributes
} from 'react';

import LoadingIcon from '@/assets/icons/loaderButton.svg';
import { cn } from '@/lib/utils/cn';

export interface I_ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  variant?:
    | 'default'
    | 'secondary'
    | 'destructive'
    | 'outline'
    | 'ghost'
    | 'link'
    | 'brand';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  isLoading?: boolean;
  icon?: ReactNode;
  iconColor?: string;
}

const variantClasses: Record<NonNullable<I_ButtonProps['variant']>, string> = {
  default:
    'bg-primary border border-transparent bg-clip-padding text-primary-foreground ' +
    'hover:bg-primary-hover focus:ring-ring active:border-1 active:border-primary-border active:ring-0 ',
  secondary:
    'bg-secondary border border-transparent bg-clip-padding text-secondary-foreground hover:bg-secondary-hover focus:ring-ring active:border-1 active:border-secondary-border active:ring-0 ',
  destructive:
    'bg-destructive border border-transparent bg-clip-padding text-destructive-foreground hover:bg-destructive-hover focus:ring-ring-destructive active:border-1 active:border-destructive-border active:ring-0 ',
  outline:
    'bg-transparent border-1 border-primary-border text-outline-foreground hover:bg-outline-hover hover:text-outline-hover-foreground focus:ring-ring active:bg-outline-pressed active:border-outline-border focus:bg-outline-focus focus:text-outline-focus-foreground active:border-1 active:border-outline-border active:ring-0 ',
  ghost:
    'bg-transparent border border-transparent bg-clip-padding text-ghost-foreground hover:bg-ghost-hover focus:ring-ring active:text-ghost-pressed-foreground  active:ring-0 active:bg-transparent ',
  link: 'bg-transparent text-link-foreground focus:ring-ring ',
  brand:
    'border border-primary bg-clip-padding pr-2 bg-[image:var(--brand-gradient)]  text-brand-foreground hover:bg-[image:var(--brand-hover-gradient)] hover:border-1 hover:border-brand-border focus:ring-ring active:border-brand-border active:border-1 active:border-brand active:ring-0 '
};

const sizeClasses: Record<NonNullable<I_ButtonProps['size']>, string> = {
  default: 'h-9 px-4 py-2 leading-5 text-base',
  icon: 'h-9 px-4 py-2 flex items-center justify-center',
  sm: 'h-8 px-3 py-2 text-sm leading-5',
  lg: 'h-10 px-8 py-2 text-xl leading-5'
};

const Button = forwardRef<HTMLButtonElement, I_ButtonProps>(
  (
    {
      asChild = false,
      className,
      variant = 'default',
      size = 'default',
      isLoading = false,
      disabled,
      children,
      icon,
      iconColor,
      type, // keep out of ...props so we control it on native <button>
      ...props
    },
    ref
  ) => {
    const isIconOnly = size === 'icon';
    const isDisabled = isLoading || disabled;

    const classes = buttonVariants({
      variant,
      size,
      className,
      isLoading,
      isIconOnly
    });

    if (asChild) {
      const onlyChild = isValidElement(children) ? (
        (children as ReactElement<HTMLAttributes<HTMLElement>>)
      ) : (
        <span>{children}</span>
      );

      return cloneElement(onlyChild, {
        className: cn(onlyChild.props.className, classes),
        'aria-disabled': isDisabled || undefined,
        tabIndex: isDisabled ? -1 : onlyChild.props?.tabIndex,
        ...props,
        ref: ref as Ref<HTMLElement>
      });
    }

    // ===== native <button> path =====
    return (
      <button
        type={type ?? 'button'}
        className={classes}
        disabled={isDisabled}
        ref={ref}
        {...props}
      >
        {isLoading && !isIconOnly ? (
          <span className="flex items-center gap-2">
            <LoadingIcon
              className="animate-spin"
              style={iconColor ? { color: iconColor } : undefined}
            />
            {children}
          </span>
        ) : isIconOnly ? (
          <span
            className="flex items-center justify-center gap-2"
            style={iconColor ? { color: iconColor } : undefined}
          >
            {icon}
            {children ? <span className="sr-only">{children}</span> : null}
          </span>
        ) : (
          <>
            {icon}
            {children}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

/**
 * Utility to generate button classes (used in Pagination, Links, etc.)
 */
function buttonVariants({
  variant = 'default',
  size = 'default',
  className,
  isLoading,
  isIconOnly
}: {
  variant?: I_ButtonProps['variant'];
  size?: I_ButtonProps['size'];
  className?: string;
  isLoading?: boolean;
  isIconOnly?: boolean;
}) {
  return cn(
    'min-h-fit leading-5 inline-flex cursor-pointer uppercase items-center justify-center font-bold rounded-lg ' +
      'transition-colors disabled:pointer-events-none disabled:opacity-[var(--opacity-disabled)] ' +
      'focus:outline-none focus:ring-3 active:opacity-[var(--opacity-pressed)]',
    variantClasses[variant ?? 'default'],
    sizeClasses[size ?? 'default'],
    isLoading && !isIconOnly
      ? 'opacity-[var(--opacity-disabled)] pointer-events-none'
      : '',
    className
  );
}

export { Button, buttonVariants };
